import { useId, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react';
import { markColor } from './primitives';
import { fmtDayLong, fmtMonth, isoDay, weekdayLabels } from '../lib/dates';
import { HOLIDAY_KINDS, type HolidayKind } from '../lib/holidays';
import { useBelow } from '../lib/useMediaQuery';

// ── Day marks: one shape per meaning, on every calendar ───────────────────────
// Four calendars drew one holiday four ways, and "dashed" meant a holiday on a
// person's record, a request waiting for HR on /me, and an optional holiday on
// the Workspace calendar (critique 2026-10-05 #1). This is the one set they all
// draw from (UI-CONVENTIONS, Vocabulary → Day marks):
//
//   a BAR is a person's leave ......... filled = approved, outline = waiting for HR
//   a SQUARE is the org's holiday ..... filled in its kind's hue; DASHED = optional;
//                                       neutral ink when a read left out its kind
//   a HATCH is a non-working day ...... stripes, no box
//   TODAY is the day's number ......... on the accent wash (MonthGrid draws it)
//
// Dashed means optional and nothing else; the hatch and the dashed optional
// holiday are the language the Workspace calendar already had. A holiday takes
// its kind's tone through markColor (≥3:1 as a mark). A leave bar takes its
// leave type's colour, which is a stored hex and so the one colour here off
// the token palette: the seeded pastels are 1.4–2.7:1 on light paper, so the
// bar mixes in ink by --hf-mark-shade (none in dark) to reach 3:1.
export type DayMarkKind = HolidayKind | 'holiday' | 'leave' | 'pending' | 'off' | 'today';

/** What each mark says, read after a name ("Diwali, public holiday"). */
const DAY_MARK_WORDS: Record<DayMarkKind, string> = {
  public: 'public holiday',
  optional: 'optional holiday',
  company: 'company holiday',
  regional: 'regional holiday',
  holiday: 'holiday',
  leave: 'approved',
  pending: 'waiting for HR',
  off: 'non-working day',
  today: 'today',
};

const LEAVE_SQUARE: CSSProperties = {
  width: 10,
  height: 10,
  borderRadius: 3,
};
const SQUARE: CSSProperties = {
  width: 'var(--hf-space-2)',
  height: 'var(--hf-space-2)',
  borderRadius: 'var(--hf-radius-xs)',
};
const leaveInk = (color: string) =>
  `color-mix(in srgb, ${color}, var(--hf-ink) var(--hf-mark-shade))`;
const HATCH =
  'repeating-linear-gradient(135deg, var(--hf-rule-control) 0 2px, transparent 2px 4px)';
// Today's number, as MonthGrid draws it.
const TODAY: CSSProperties = {
  background: 'var(--hf-accent-wash)',
  color: 'var(--hf-accent-text)',
  fontWeight: 'var(--hf-weight-semibold)',
};

/**
 * The paint of one day mark. `color` is a leave type's (a stored hex or a
 * token); every other kind brings its own, and a leave with none is neutral.
 * For `today` it is the number's paint, not a swatch.
 */
// Beside DayMark on purpose: one set, one file. Nothing here holds state.
// eslint-disable-next-line react-refresh/only-export-components
export const dayMarkStyle = (kind: DayMarkKind, color = 'var(--hf-ink-3)'): CSSProperties => {
  const mark: CSSProperties = { display: 'inline-block', flexShrink: 0 };
  switch (kind) {
    case 'today':
      return TODAY;
    // A tinted square in the type's colour, as the leave calendar's cells are
    // tinted (owner, 2026-10-09: the earlier style, "use this style").
    case 'leave':
      return {
        ...mark,
        ...LEAVE_SQUARE,
        background: `color-mix(in srgb, ${color} 30%, transparent)`,
        border: `1px solid ${leaveInk(color)}`,
      };
    case 'pending':
      return {
        ...mark,
        ...LEAVE_SQUARE,
        background: 'transparent',
        // Outlined, not dashed: dashed is only an optional holiday.
        border: `1px solid ${leaveInk(color)}`,
      };
    case 'off':
      return { ...mark, ...SQUARE, background: HATCH };
    // A holiday whose kind the caller didn't read: the square, in no kind's
    // hue. Drawn as the default kind, Holi (optional) was a public square.
    case 'holiday':
      return { ...mark, ...SQUARE, background: 'var(--hf-ink-3)' };
    case 'optional':
      return {
        ...mark,
        ...SQUARE,
        background: 'transparent',
        border: `1px dashed ${markColor(HOLIDAY_KINDS.optional.tone)}`,
      };
    default:
      return { ...mark, ...SQUARE, background: markColor(HOLIDAY_KINDS[kind].tone) };
  }
};

/**
 * One day mark and its words. In a legend the words show ("Waiting for HR");
 * in a day cell, where the mark is all a sighted person sees, `srOnly` keeps
 * them for a screen reader, after `name` ("Casual leave, waiting for HR").
 * `children` replaces the words, for a legend's leave types ("Casual").
 */
export const DayMark = ({
  kind,
  color,
  name,
  srOnly,
  children,
}: {
  kind: DayMarkKind;
  /** A leave type's colour, for `leave` and `pending`. */
  color?: string;
  /** What is on the day: a holiday's or a leave type's name. */
  name?: string;
  srOnly?: boolean;
  children?: ReactNode;
}) => {
  const said = DAY_MARK_WORDS[kind];
  const words =
    children ?? (name ? `${name}, ${said}` : said.charAt(0).toUpperCase() + said.slice(1));
  if (kind === 'today')
    return (
      <span
        style={{
          ...TODAY,
          padding: '0 var(--hf-space-1)',
          borderRadius: 'var(--hf-radius-pill)',
          fontFamily: 'var(--hf-font-mono)',
        }}
      >
        {words}
      </span>
    );
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--hf-space-2)',
        minWidth: 0,
      }}
    >
      <span aria-hidden="true" style={dayMarkStyle(kind, color)} />
      <span className={srOnly ? 'hf-sr-only' : undefined}>{words}</span>
    </span>
  );
};

/**
 * The holiday kinds `holidays` holds, in HOLIDAY_KINDS order. A key names what
 * its calendar draws: the Workspace keyed all four kinds whatever the month
 * held, and Org keyed Regional in a year with none (critique 2026-10-06 #2).
 * A bare string (a holiday with no kind) holds none.
 */
// Beside DayMark on purpose, as dayMarkStyle is: one set, one file.
// eslint-disable-next-line react-refresh/only-export-components
export const holidayKindsIn = (
  holidays: Iterable<{ kind?: HolidayKind } | string | null | undefined>,
): HolidayKind[] => {
  const held = new Set<HolidayKind | undefined>();
  for (const h of holidays) if (h && typeof h === 'object') held.add(h.kind);
  return (Object.keys(HOLIDAY_KINDS) as HolidayKind[]).filter((k) => held.has(k));
};

/**
 * A calendar's key, at its foot (UI-CONVENTIONS, Day marks): under a hairline,
 * at text-xs in ink-3. It keys only what the calendar draws: each leave type
 * given, "Waiting for HR" only while `pending`, a holiday kind only if
 * `holidays` holds one (and "Holiday" for one with no kind: a bare name, or a
 * kind left out), and the hatch only where a day is `off`. Nothing to key
 * renders nothing.
 */
export const DayMarkKey = ({
  leaveTypes = [],
  pending,
  holidays = [],
  off,
}: {
  leaveTypes?: readonly { label: string; color?: string }[];
  pending?: boolean;
  holidays?: Iterable<{ kind?: HolidayKind } | string | null | undefined>;
  off?: boolean;
}) => {
  const list = [...holidays];
  const kinds = holidayKindsIn(list);
  const kindless = list.some((h) => typeof h === 'string' || (h != null && !h.kind));
  if (!leaveTypes.length && !pending && !kinds.length && !kindless && !off) return null;
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 'var(--hf-space-2) var(--hf-space-3)',
        marginTop: 'var(--hf-space-3)',
        paddingTop: 'var(--hf-space-3)',
        borderTop: '1px solid var(--hf-rule)',
        fontSize: 'var(--hf-text-xs)',
        color: 'var(--hf-ink-3)',
      }}
    >
      {leaveTypes.map((t, i) => (
        <DayMark key={`${i}-${t.label}`} kind="leave" color={t.color}>
          {t.label}
        </DayMark>
      ))}
      {pending && <DayMark kind="pending" />}
      {kinds.map((k) => (
        <DayMark key={k} kind={k} />
      ))}
      {kindless && <DayMark kind="holiday" />}
      {off && <DayMark kind="off" />}
    </div>
  );
};

// ── MonthGrid ─────────────────────────────────────────────────────────────────
// One month as a keyboard grid, for the Workspace calendar, the leave calendar
// and the Org holiday mini-months (adopted in Phase 3). Each of those built its
// own: day cells were divs with no role or label, a five-day event was five Tab
// stops, a busy day hid three events in a 132px nested scroller with no sign
// they were there (WS-07, WS-13), and the holiday calendar put 358 day buttons
// between the user and the Add form (ORG-10).
//
// The grid is ONE Tab stop: the selected day, else today, else the 1st, and
// then wherever focus last was in this month. Arrows move by day and week,
// Home/End to the ends of the week row, PageUp/PageDown to the first/last day
// of the month — the month on either side belongs to the parent's
// PeriodStepper, so an arrow that would leave the month does nothing (it used
// to clamp, and ArrowDown from Fri 25 landed on Wed 30, in another column).
// Enter, Space or a click selects the day.
//
// A cell is the only interactive thing in it: `renderDay` returns non-interactive
// content (chips, dots), at most `maxPerDay` of which are drawn before "+N more".
// Everything it returns is read as the cell's description, after its date.
//
// Below md a day is ~45px wide and a chip in it is one letter (WS-05), so the
// grid goes `compact` by itself there: short cells, items in a row, the overflow
// a bare "+N". `renderDay` is told, so it can return a mark per item (a dot,
// the words in .hf-sr-only) instead of text, and `onSelectDay` is how the screen
// opens the day in full — on a phone, in a Dialog, which is a bottom sheet there.
export const MonthGrid = ({
  year,
  month,
  weekStartsOn,
  renderDay,
  onSelectDay,
  compact: compactProp,
  maxPerDay = 3,
  label,
  selectedKey,
  today,
  tintOf,
}: {
  year: number;
  /** 0–11, as Date's month. */
  month: number;
  /** getDay() index of the org's first weekday: weekStartIndex(week_start). */
  weekStartsOn: number;
  /** The day's items, from its 'YYYY-MM-DD' key. `compact`: draw marks, not text. */
  renderDay: (key: string, compact: boolean) => ReactNode[];
  onSelectDay?: (key: string) => void;
  /** Short cells with items laid in a row (dots rather than chips). Unset, it
   *  follows the width: compact below md. */
  compact?: boolean;
  maxPerDay?: number;
  /** The grid's name; defaults to "September 2026". */
  label?: string;
  /** 'YYYY-MM-DD' of the chosen day: aria-selected, and the first Tab stop.
   *  Leave it out where days aren't chosen, so no cell reads "not selected". */
  selectedKey?: string;
  /** 'YYYY-MM-DD' of the org's today (orgDateKey): aria-current="date". */
  today?: string;
  /**
   * A day the whole cell is about — leave — tinted in that colour, its number
   * too, only outlined while it waits (owner, 2026-10-09: the earlier leave
   * calendar's look). The words stay in renderDay, for a screen reader.
   */
  tintOf?: (key: string) => { color: string; outlined?: boolean } | null;
}) => {
  const id = useId();
  const phone = useBelow('md');
  const compact = compactProp ?? phone;
  const days = new Date(year, month + 1, 0).getDate();
  const lead = (new Date(year, month, 1).getDay() - weekStartsOn + 7) % 7;
  const dayOf = (key?: string) => {
    const [y, m, d] = (key ?? '').split('-').map(Number);
    return y === year && m === month + 1 ? d : undefined;
  };
  // Where focus last was, kept per month: a new month starts from its own
  // selected day or today rather than the old month's day number.
  const ym = year * 12 + month;
  const [focus, setFocus] = useState<{ ym: number; day: number } | null>(null);
  const active = focus?.ym === ym ? focus.day : (dayOf(selectedKey) ?? dayOf(today) ?? 1);
  const col = (d: number) => (lead + d - 1) % 7;

  const cells = [...Array<null>(lead).fill(null), ...Array.from({ length: days }, (_, i) => i + 1)];
  while (cells.length % 7) cells.push(null);
  const weeks = Array.from({ length: cells.length / 7 }, (_, w) => cells.slice(w * 7, w * 7 + 7));

  // The grid's and each day's name come from lib/dates, in its fixed English
  // and its day-month order: Intl.DateTimeFormat(undefined, …) named them in
  // the browser's locale, so one month read two ways on two machines (§2).
  const name = label ?? fmtMonth(year, month);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const to = (
      {
        ArrowLeft: active - 1,
        ArrowRight: active + 1,
        ArrowUp: active - 7,
        ArrowDown: active + 7,
        // The row's ends within the month: the first row starts after blanks.
        Home: Math.max(1, active - col(active)),
        End: Math.min(days, active + 6 - col(active)),
        PageUp: 1,
        PageDown: days,
      } as Record<string, number>
    )[e.key];
    if (to == null) return;
    e.preventDefault();
    if (to < 1 || to > days) return;
    setFocus({ ym, day: to });
    e.currentTarget.querySelector<HTMLElement>(`[data-day="${to}"]`)?.focus();
  };

  const cell = (d: number) => {
    const key = isoDay(new Date(year, month, d));
    const items = renderDay(key, compact);
    const more = items.length - maxPerDay;
    const itemsId = `${id}-${d}`;
    const selected = key === selectedKey;
    const isToday = key === today;
    const tint = tintOf?.(key) ?? null;
    return (
      <button
        key={key}
        type="button"
        role="gridcell"
        data-day={d}
        tabIndex={d === active ? 0 : -1}
        aria-label={fmtDayLong(key)}
        aria-describedby={items.length ? itemsId : undefined}
        aria-selected={selectedKey ? selected : undefined}
        aria-current={isToday ? 'date' : undefined}
        // Its height is in tokens.css (.hf-day): 44px under a finger when compact.
        className="hf-day"
        data-compact={compact || undefined}
        // However focus arrived (a click, a screen reader's cursor), the arrows
        // start from where it is.
        onFocus={() => d !== active && setFocus({ ym, day: d })}
        onClick={() => {
          setFocus({ ym, day: d });
          onSelectDay?.(key);
        }}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: 'var(--hf-space-1)',
          minWidth: 0,
          padding: 'var(--hf-space-1)',
          textAlign: 'left',
          overflow: 'hidden',
          background: tint
            ? tint.outlined
              ? 'transparent'
              : `color-mix(in srgb, ${tint.color} 14%, transparent)`
            : 'transparent',
          border: tint
            ? `1px solid color-mix(in srgb, ${tint.color} ${tint.outlined ? 70 : 45}%, transparent)`
            : '1px solid var(--hf-rule)',
          borderRadius: 'var(--hf-radius-sm)',
          // Selection is accent (UI-CONVENTIONS §1), as the calendar drew it;
          // a shadow, so the global :focus-visible outline still shows on top.
          boxShadow: selected ? 'inset 0 0 0 2px var(--hf-accent)' : undefined,
          color: 'var(--hf-ink)',
          font: 'inherit',
          cursor: onSelectDay ? 'pointer' : 'default',
        }}
      >
        <span
          aria-hidden="true"
          style={{
            alignSelf: 'flex-start',
            padding: '0 var(--hf-space-1)',
            borderRadius: 'var(--hf-radius-pill)',
            fontFamily: 'var(--hf-font-mono)',
            fontSize: 'var(--hf-text-xs)',
            ...(isToday
              ? dayMarkStyle('today')
              : tint
                ? { color: tint.color, fontWeight: 'var(--hf-weight-medium)' }
                : { color: 'var(--hf-ink-2)' }),
          }}
        >
          {d}
        </span>
        {items.length > 0 && (
          <span
            id={itemsId}
            style={{
              display: 'flex',
              flexDirection: compact ? 'row' : 'column',
              flexWrap: 'wrap',
              gap: 'var(--hf-space-1)',
              minWidth: 0,
            }}
          >
            {items.slice(0, maxPerDay).map((x, i) => (
              <span key={i} style={{ display: 'block', minWidth: 0 }}>
                {x}
              </span>
            ))}
            {/* "+3 more" doesn't fit a phone's cell, and was cut mid-word. The
                count alone there, still read in full. */}
            {more > 0 && (
              <span style={{ fontSize: 'var(--hf-text-xs)', color: 'var(--hf-ink-3)' }}>
                +{more}
                {compact ? (
                  <>
                    {' '}
                    <span className="hf-sr-only">more</span>
                  </>
                ) : (
                  ' more'
                )}
              </span>
            )}
          </span>
        )}
      </button>
    );
  };

  const row = {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
    gap: 'var(--hf-space-1)',
  };
  return (
    <div
      role="grid"
      aria-label={name}
      onKeyDown={onKeyDown}
      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hf-space-1)' }}
    >
      <div role="row" style={row}>
        {weekdayLabels(weekStartsOn).map((l) => (
          <div
            key={l}
            role="columnheader"
            style={{
              textAlign: 'center',
              fontSize: 'var(--hf-text-2xs)',
              color: 'var(--hf-ink-3)',
            }}
          >
            {l}
          </div>
        ))}
      </div>
      {weeks.map((w, i) => (
        <div key={i} role="row" style={row}>
          {w.map((d, j) => (d == null ? <div key={`_${j}`} role="gridcell" /> : cell(d)))}
        </div>
      ))}
    </div>
  );
};
